/**
 * 全站功能引导步骤配置（data-ebk-guide 锚点）
 */

export const GUIDE_STORAGE_PREFIX = "ebk_guide_v1";

export const GUIDE_IDS = {
  overview: "overview",
  app_nav: "app_nav",
  hotel: "hotel",
  roomType: "roomType",
  order: "order",
  payRvDetailList: "payRvDetailList",
};

/** 路由 path → 页面引导 id（顶栏「新手指引」用） */
export const ROUTE_GUIDE_MAP = {
  "/hotel": GUIDE_IDS.hotel,
  "/roomType": GUIDE_IDS.roomType,
  "/order": GUIDE_IDS.order,
  "/payRvDetailList": GUIDE_IDS.payRvDetailList,
};

/**
 * @typedef {Object} GuideStepDef
 * @property {string} [anchor] data-ebk-guide 值
 * @property {string} title
 * @property {string} description
 * @property {boolean} [requireMaintain] 仅可维护账号展示
 * @property {boolean} [skipInEmbed] 嵌入模式（无侧栏）跳过
 * @property {boolean} [optional] 锚点不存在时跳过（默认 true）
 * @property {'top'|'right'|'bottom'|'left'|'over'} [side]
 */

/** @type {Record<string, GuideStepDef[]>} */
export const FEATURE_GUIDE_STEPS = {
  [GUIDE_IDS.app_nav]: [
    {
      anchor: "nav-menu",
      title: "功能导航",
      description:
        "电脑端使用左侧菜单；手机端先点左上角「菜单」图标打开导航，可切换酒店列表、订单列表、结算列表。日常从「酒店列表」进入维护房型与价格。",
      side: "right",
      skipInEmbed: true,
    },
    {
      anchor: "nav-order-badge",
      title: "新订单提醒",
      description:
        "订单菜单上的数字表示待处理订单数，系统会定时刷新；请及时进入订单列表确认或处理。",
      optional: true,
      skipInEmbed: true,
    },
    {
      anchor: "readonly-hint",
      title: "只读账号说明",
      description:
        "若看到此提示，表示当前账号仅可查看酒店、房型与价格，不能新增或修改酒店/房型/政策/房价房态。",
      optional: true,
    },
  ],
  [GUIDE_IDS.hotel]: [
    {
      anchor: "hotel-filter",
      title: "筛选酒店",
      description: "按国家、城市、品牌、等级、销售类型或名称筛选，缩小列表范围。",
    },
    {
      anchor: "hotel-search",
      title: "查询列表",
      description: "设置条件后点击「查询」，刷新下方酒店列表。",
    },
    {
      anchor: "hotel-room-btn",
      title: "进入房型政策",
      description:
        "点击某一酒店的「查看房型」，进入该酒店的房型政策页，维护政策、房价与房态。",
    },
    {
      anchor: "hotel-add",
      title: "新增酒店",
      description: "直签自营供应商可在此检索并绑定母酒店，新增可售酒店。",
      requireMaintain: true,
    },
  ],
  [GUIDE_IDS.roomType]: [
    {
      anchor: "room-hotel-card",
      title: "当前酒店",
      description: "展示已选酒店的基本信息、地址、备注与介绍；备注失焦后自动保存。",
      mobilePosition: "bottom-center",
    },
    {
      anchor: "room-policy-toolbar",
      title: "查询房型政策",
      description: "选择起始日期与周期（按周/按月），点击「查询政策」加载房型与每日房价房态。",
    },
    {
      anchor: "room-add-type",
      title: "新增房型",
      description: "为当前酒店添加新房型；添加后可在下方展开面板中维护政策。",
      requireMaintain: true,
    },
    {
      anchor: "room-week-board",
      title: "房型与政策面板",
      description:
        "每个房型可展开查看政策与日期网格；点击房型名称可查看房型详情，可「添加政策」。",
      mobilePosition: "bottom-center",
      optional: true,
    },
    {
      anchor: "room-price-status",
      title: "维护房价与房态",
      description: "在日期格中点击「房价」或「房态」可批量或单日修改售价与开房状态。",
      optional: true,
    },
  ],
  [GUIDE_IDS.order]: [
    {
      anchor: "order-filter",
      title: "筛选订单",
      description: "按订单状态、下单时间、酒店名称、单据号或联系电话筛选。",
    },
    {
      anchor: "order-search",
      title: "查询订单",
      description: "点击「查询」刷新列表；移动端可先展开「筛选条件」。",
    },
    {
      anchor: "order-table",
      title: "处理订单",
      description: "在列表中查看订单摘要，点击进入详情进行确认、取消等操作。",
      optional: true,
    },
  ],
  [GUIDE_IDS.payRvDetailList]: [
    {
      anchor: "payrv-filter",
      title: "筛选结算单",
      description: "按结算分类、发起时间、结算单号筛选历史结算记录。",
    },
    {
      anchor: "payrv-create",
      title: "发起结算",
      description: "选择待结算明细并发起新的结算申请。",
    },
    {
      anchor: "payrv-setting",
      title: "自动结算设置",
      description: "配置结算账户、发票信息等，发起结算前建议先完成设置。",
    },
    {
      anchor: "payrv-table",
      title: "结算列表",
      description: "查看各结算单状态，可进入详情上传发票或跟踪审批进度。",
      optional: true,
    },
  ],
};

export function getGuideIdForRoute(path) {
  if (!path) return null;
  const p = String(path).split("?")[0];
  return ROUTE_GUIDE_MAP[p] || null;
}



// WEBPACK FOOTER //
// ./src/config/featureGuides.js